import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TopCurveProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 円の直径（px） */
  diameter?: number;
  /** 背景画像URL */
  backgroundImage?: string;
  /** オーバーレイ色（RGBA） */
  overlayColor?: string;
  /** 背景画像のblur量（px） */
  backgroundBlur?: number;
}

export interface TopCurveContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** コンテナの高さ */
  height?: string | number;
}

/**
 * TopCurve - 正円背景デコレーションコンポーネント
 *
 * 正円を背景として配置し、ClubLayoutのmax-width(432px)からはみ出した部分を
 * overflow:hiddenでクリッピングします。ページトップの装飾として使用します。
 * 円の上部がContainerの頂点に配置されます。
 *
 * @example
 * ```tsx
 * // 基本的な使い方（ClubLayoutで使用）
 * <ClubLayout>
 *   <TopCurveContainer>
 *     <TopCurve className="bg-[#F6F5F4]" diameter={584} />
 *     <div>コンテンツ</div>
 *   </TopCurveContainer>
 * </ClubLayout>
 *
 * // サイズを指定
 * <TopCurve className="bg-blue-500" diameter={400} />
 *
 * // グラデーション
 * <TopCurve
 *   className="bg-gradient-to-br from-[#F6F5F4] to-white"
 *   diameter={584}
 * />
 * ```
 */
export const TopCurve = React.forwardRef<HTMLDivElement, TopCurveProps>(
  (
    {
      className,
      diameter = 584,
      backgroundImage,
      overlayColor,
      backgroundBlur,
      style,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'absolute pointer-events-none rounded-full z-0',
          'top-0 left-1/2 -translate-x-1/2',
          className,
        )}
        style={{
          width: `${diameter}px`,
          height: `${diameter}px`,
          overflow: 'hidden',
          ...style,
        }}
        {...props}
      >
        {/* 背景画像レイヤー */}
        {backgroundImage && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: backgroundBlur ? `blur(${backgroundBlur}px)` : undefined,
              opacity: 0.2,
              transform: 'rotate(90deg)',
            }}
          />
        )}
        {/* オーバーレイ */}
        {overlayColor && (
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: overlayColor,
            }}
          />
        )}
      </div>
    );
  },
);

TopCurve.displayName = 'TopCurve';

/**
 * TopCurveContainer - TopCurveを配置するためのコンテナ
 *
 * ClubLayout内でTopCurveを使用する際に、relativeポジションとoverflowを提供します
 *
 * @example
 * ```tsx
 * // デフォルト
 * <TopCurveContainer>
 *   <TopCurve className="bg-[#F6F5F4]" diameter={584} />
 *   <div>コンテンツ</div>
 * </TopCurveContainer>
 *
 * // 高さを指定
 * <TopCurveContainer height={300}>
 *   <TopCurve className="bg-[#F6F5F4]" diameter={584} />
 * </TopCurveContainer>
 *
 * // Tailwindのクラスで指定
 * <TopCurveContainer className="h-64">
 *   <TopCurve className="bg-[#F6F5F4]" diameter={584} />
 * </TopCurveContainer>
 * ```
 */
export const TopCurveContainer = React.forwardRef<
  HTMLDivElement,
  TopCurveContainerProps
>(({ className, children, height, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('relative overflow-hidden', className)}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
});

TopCurveContainer.displayName = 'TopCurveContainer';
